import React, {useRef, useCallback} from 'react';
import {cloneDeep} from 'lodash';
import {useProductContext} from 'contexts';
import {mapProductData} from 'components/customer/Product/helpers/mapProductData';
import type {
    CabinetMesh,
    RoomPlannerProduct,
} from 'components/customer/RoomPlanner/types';
import type {PreviewFormValues} from 'components/customer/Preview3D/types';

const ensureRequiredForSave = (
    payload: Record<string, unknown>
): Record<string, unknown> => {
    const out = {...payload};
    if (
        out.drawer_face_height === undefined ||
        out.drawer_face_height === null
    ) {
        out.drawer_face_height = [];
    }
    if (!Array.isArray(out.drawer_face_height)) {
        out.drawer_face_height = [out.drawer_face_height].filter(
            (v): v is number => typeof v === 'number'
        );
    }
    return out;
};

const normalizeShelvesForSave = (payload: Record<string, unknown>): void => {
    const shelvesArray = payload.shelves;
    const isArrayOfObjects =
        Array.isArray(shelvesArray) &&
        shelvesArray.length > 0 &&
        typeof shelvesArray[0] === 'object' &&
        shelvesArray[0] !== null &&
        ('shelf_type' in shelvesArray[0] ||
            'shelf_style' in shelvesArray[0] ||
            'shelf_position' in shelvesArray[0] ||
            'shelf_offset' in shelvesArray[0]);

    if (!isArrayOfObjects) return;

    const shelfFields = [
        'shelf_type',
        'shelf_style',
        'shelf_position',
        'shelf_offset',
    ] as const;
    const simpleShelfStyle =
        payload.cabinet_simple_shelves &&
        payload.simple_shelf_type !== undefined &&
        payload.simple_shelf_type !== null
            ? Number(payload.simple_shelf_type)
            : undefined;

    shelfFields.forEach((field) => {
        const arr: number[] = [];
        for (let i = 0; i < shelvesArray.length; i++) {
            const shelf = shelvesArray[Number(i)] as Record<string, unknown>;
            if (field.indexOf('position') > -1) {
                const v = shelf[String(field)];
                arr.push(typeof v === 'number' ? v : Number(v) || 0.5);
            } else if (
                field.indexOf('shelf_style') > -1 &&
                typeof simpleShelfStyle === 'number'
            ) {
                arr.push(simpleShelfStyle);
            } else {
                const val = shelf[String(field)];
                arr.push(typeof val === 'number' ? val : Number(val) || 0);
            }
        }
        payload[String(field)] = arr;
    });
    payload.shelves = [];
};

const useProductValuesMapping = () => {
    const {productDataStore} = useProductContext();
    const shelvesStructureStore = useRef<Record<string, unknown>>({});
    const shelfTypes = useRef<Record<string, unknown>>({});
    const shelfStyles = useRef<Record<string, unknown>>({});

    const getMappedValues = useCallback(
        (
            source:
                | CabinetMesh
                | (RoomPlannerProduct & Record<string, unknown>)
                | null
                | undefined
        ): PreviewFormValues | undefined => {
            const values =
                source && 'userData' in source
                    ? (source as CabinetMesh).userData?.values
                    : (source as RoomPlannerProduct | undefined);

            if (!values || typeof values !== 'object') {
                return undefined;
            }

            const productStore = productDataStore?.current as
                | {cabinet?: {attributes?: unknown}}
                | undefined;
            if (!productStore?.cabinet?.attributes) {
                const cloned = cloneDeep(values) as unknown as Record<
                    string,
                    unknown
                >;
                normalizeShelvesForSave(cloned);
                return ensureRequiredForSave(
                    cloned
                ) as unknown as PreviewFormValues;
            }

            try {
                const mapped = mapProductData({
                    values: cloneDeep(values) as unknown as Record<
                        string,
                        unknown
                    >,
                    productDataStore:
                        productDataStore as unknown as React.MutableRefObject<
                            Record<string, unknown>
                        >,
                    shelvesStructureStore:
                        shelvesStructureStore as unknown as React.MutableRefObject<
                            Record<string, unknown>
                        >,
                    shelfTypes: shelfTypes as unknown as React.MutableRefObject<
                        Record<string, unknown>
                    >,
                    shelfStyles:
                        shelfStyles as unknown as React.MutableRefObject<
                            Record<string, unknown>
                        >,
                }) as Record<string, unknown>;
                return ensureRequiredForSave(
                    mapped
                ) as unknown as PreviewFormValues;
            } catch (e) {
                const cloned = cloneDeep(values) as unknown as Record<
                    string,
                    unknown
                >;
                normalizeShelvesForSave(cloned);
                return ensureRequiredForSave(
                    cloned
                ) as unknown as PreviewFormValues;
            }
        },
        [productDataStore]
    );

    return {
        getMappedValues,
        productDataStore,
        shelvesStructureStore,
        shelfTypes,
        shelfStyles,
    };
};

export default useProductValuesMapping;
